import React, { useEffect, useId, useState } from 'react';
import DatePicker, { CalendarContainer } from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import PropTypes from 'prop-types';
import moment from 'moment';
import './index.css';
import { ReactComponent as CalendarSvg } from '../../../../../assets/icons/calendar.svg';

const calendarData = {
  days: ['s', 'm', 't', 'w', 't', 'f', 's'],
  months: ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'],
};

const locale = {
  localize: {
    day: (n) => calendarData.days[n],
    month: (n) => calendarData.months[n],
  },
  formatLong: {
    date: () => 'yyyy-mm-dd',
  },
};

const today = moment().format('MM/DD/YYYY');
const yesterday = moment(new Date()).subtract(1, 'd').format('MM/DD/YYYY');
const weekStartDay = moment().subtract(7, 'day').format('MM/DD/YYYY');
const thisMonthStartDay = moment().startOf('month').format('MM/DD/YYYY');
const thisMonthEndDate = today;
const lastMonthStartDay = moment().subtract(1, 'month').startOf('month').format('MM/DD/YYYY');
const lastMonthEndDate = moment().subtract(1, 'months').endOf('month').format('MM/DD/YYYY');
const lastThreeMonthStartDay = moment().subtract(3, 'months').startOf('month').format('MM/DD/YYYY');
const thisYearStartDay = moment().startOf('year').format('MM/DD/YYYY');

const tabRange = [
  {
    tabName: 'today',
    start: new Date(),
    end: null,
    dateArray: [today, null],
  },
  {
    tabName: 'yesterday',
    start: new Date(yesterday),
    end: null,
    dateArray: [yesterday, null],
  },
  {
    tabName: 'week',
    start: new Date(weekStartDay),
    end: new Date(),
    dateArray: [weekStartDay, today],
  },
  {
    tabName: 'thisMonth',
    start: new Date(thisMonthStartDay),
    end: new Date(),
    dateArray: [thisMonthStartDay, today],
  },
  {
    tabName: 'lastMonth',
    start: new Date(lastMonthStartDay),
    end: new Date(lastMonthEndDate),
    dateArray: [lastMonthStartDay, lastMonthEndDate],
  },
  {
    tabName: 'last3Months',
    start: new Date(lastThreeMonthStartDay),
    end: new Date(),
    dateArray: [lastThreeMonthStartDay, today],
  },
  {
    tabName: 'yearToDate',
    start: new Date(thisYearStartDay),
    end: new Date(),
    dateArray: [thisYearStartDay, today],
  },
];

let date = {};

export const RangeDatePicker = ({
  currentDate, onChangeDate, show, withoutApplyBtn, todayLabel, showMonthsCount = 2, selectsRange, showLastYear,
  emptyAfterClear,
}) => {
  const uniqId = useId();
  const [startDate, setStartDate] = useState(currentDate.start ? new Date(currentDate.start) : null);
  const [endDate, setEndDate] = useState(currentDate.end ? new Date(currentDate.end) : null);

  const [isCalendarOpen, calendarToggle] = useState(false);

  const findTab = tabRange.reverse().find((t) => currentDate.start === moment(t.dateArray[0]).format('YYYY-MM-DD')
    && (currentDate.end || null) === (t.dateArray[1] ? moment(t.dateArray[1]).format('YYYY-MM-DD') : null));

  const [currentTab, setCurrentTab] = useState(findTab ? findTab.tabName : '');

  useEffect(() => {
    if (!isCalendarOpen) {
      date = currentDate;

      setStartDate(currentDate.start ? new Date(moment(currentDate.start).format('MM/DD/YYYY')) : null);
      setEndDate(currentDate.end ? new Date(moment(currentDate.end).format('MM/DD/YYYY')) : null);

      setCurrentTab(findTab?.tabName || '');
    }
  }, [currentDate]);

  const closeCalendar = (clear) => {
    calendarToggle(false);

    if (!date.start) {
      setStartDate(null);
      setEndDate(null);
    } else {
      setStartDate(new Date(date.start));
      setEndDate(date.end ? new Date(date.end) : null);
    }

    if (clear) {
      setStartDate(!emptyAfterClear ? new Date(thisMonthStartDay) : null);
      setEndDate(!emptyAfterClear ? new Date(thisMonthEndDate) : null);

      onChangeDate([
        !emptyAfterClear ? moment(thisMonthStartDay).format('YYYY-MM-DD') : '',
        !emptyAfterClear ? moment(thisMonthEndDate).format('YYYY-MM-DD') : '',
      ]);
    }
  };

  const calendarOpen = (toggle) => {
    if (!toggle) closeCalendar();

    else calendarToggle(toggle);
  };

  const onClickTabs = (tab) => {
    const clickedTab = tabRange.find((t) => t.tabName === tab);

    setStartDate(clickedTab.start);
    setEndDate(clickedTab.end);

    onChangeDate([
      moment(clickedTab.dateArray[0]).format('YYYY-MM-DD'),
      clickedTab.dateArray[1] ? moment(clickedTab.dateArray[1]).format('YYYY-MM-DD') : null]);

    calendarToggle(false);
  };

  const onChange = (dates) => {
    if (!selectsRange) {
      const [start, end] = dates;
      setStartDate(start);
      setEndDate(end);

      if (withoutApplyBtn) {
        onChangeDate([moment(start).format('YYYY-MM-DD'),
          end ? moment(end).format('YYYY-MM-DD') : null]);
      }
    } else {
      setStartDate(dates);
      onChangeDate(moment(dates).format('YYYY-MM-DD'));
    }
  };

  const onApplyClick = () => {
    if (startDate) {
      onChangeDate([moment(startDate).format('YYYY-MM-DD'),
        endDate ? moment(endDate).format('YYYY-MM-DD') : null]);
    }

    calendarToggle(false);
  };

  const onCancelClick = () => {
    closeCalendar(true);
  };

  const closeHandler = (e) => {
    if (!e.target.closest(`[data-id="calendar_${uniqId}"]`) && !show) {
      closeCalendar();
    }
  };

  useEffect(() => {
    window.addEventListener('click', closeHandler, true);

    return () => {
      window.removeEventListener('click', closeHandler, true);
    };
  }, []);

  const calendarContainer = ({ className, children }) => (
    <div className="date_piker_wrapper">
      <CalendarContainer className={className}>
        <div style={{ position: 'relative' }}>{children}</div>

        {showMonthsCount !== 1 && (
        <div className="bottom_buttons">

          <div className="date_buttons">
            <button
              type="button"
              className={currentTab === 'today' ? 'active' : ''}
              onClick={() => onClickTabs('today')}
            >
              Today
            </button>

            <button
              type="button"
              className={currentTab === 'yesterday' ? 'active' : ''}
              onClick={() => onClickTabs('yesterday')}
            >
              Yesterday
            </button>

            <button
              type="button"
              className={currentTab === 'week' ? 'active' : ''}
              onClick={() => onClickTabs('week')}
            >
              Last 7 days
            </button>

            <button
              type="button"
              className={currentTab === 'thisMonth' ? 'active' : ''}
              onClick={() => onClickTabs('thisMonth')}
            >
              This month
            </button>

            <button
              type="button"
              className={currentTab === 'lastMonth' ? 'active' : ''}
              onClick={() => onClickTabs('lastMonth')}
            >
              Last month
            </button>

            {showLastYear && (
              <>
                <button
                  type="button"
                  className={currentTab === 'last3Months' ? 'active' : ''}
                  onClick={() => onClickTabs('last3Months')}
                >
                  Last 3 month
                </button>

                <button
                  type="button"
                  className={currentTab === 'yearToDate' ? 'active' : ''}
                  onClick={() => onClickTabs('yearToDate')}
                >
                  Year to date
                </button>
              </>
            )}
          </div>

          <div className="apply_buttons">
            <button
              type="button"
              onClick={onCancelClick}
            >
              Clear
            </button>

            {!withoutApplyBtn && (
            <button
              type="button"
              className="apply_btn"
              onClick={onApplyClick}
            >
              Apply
            </button>
            )}
          </div>

        </div>
        )}
      </CalendarContainer>
    </div>
  );

  return (
    <div data-id={`calendar_${uniqId}`} className={`calendar_wrapper ${show ? 'inline' : ''}`}>
      <DatePicker
        customInput={(
          <button
            className={`calendar_custom_input ${isCalendarOpen ? 'active' : ''}`}
            onClickCapture={() => calendarOpen(!isCalendarOpen)}
            type="button"
          >
            <CalendarSvg className="calendar_icon" />

            <div>
              {currentDate.start
                ? (
                  <span>
                    {currentDate.end
                      ? `${moment(currentDate.start).format('MM.DD.YYYY')} 
                      - ${moment(currentDate.end).format('MM.DD.YYYY')}`
                      : todayLabel && currentTab === 'today'
                        ? 'Today'
                        : moment(currentDate.start).format('MM.DD.YYYY')}
                  </span>
                )

                : <span style={{ color: '#949292' }}>mm.dd.yyyy - mm.dd.yyyy</span>}
            </div>

            <div
              className={`calendar_custom_input_arrow ${isCalendarOpen ? 'open' : ''}`}
            />
          </button>
        )}
        selected={startDate ? new Date(moment(startDate).format('MM/DD/YYYY')) : null}
        startDate={startDate ? new Date(moment(startDate).format('MM/DD/YYYY')) : null}
        endDate={endDate ? new Date(moment(endDate).format('MM/DD/YYYY')) : null}
        selectsRange={!selectsRange}
        onChange={onChange}
        yearDropdownItemNumber={100}
        showYearDropdown={showMonthsCount !== 1}
        monthsShown={showMonthsCount}
        locale={locale}
        inline={show}
        scrollableYearDropdown
        open={isCalendarOpen}
        calendarContainer={calendarContainer}
      />
    </div>
  );
};

RangeDatePicker.propTypes = {
  currentDate: PropTypes.object.isRequired,
  onChangeDate: PropTypes.func.isRequired,
  show: PropTypes.bool,
  todayLabel: PropTypes.bool,
  withoutApplyBtn: PropTypes.bool,
  selectsRange: PropTypes.bool,
  showMonthsCount: PropTypes.number,
};
